import React, { FC, useEffect, useRef } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { isEmpty, isEqual } from 'lodash';
import { ProgressBar } from '../../../components';
import {
  getCompletedSections,
  getNewlyCompletedSectionNameToCompletionTime,
  getProgressBarCompletionStates,
  getRelevantSections,
  getSectionsToFormValuesAndErrors,
  trackCompletedListingSections,
} from './ListingFlowProgressBar.utils';
import { useOwnListingVersions } from '../hooks/useOwnListingVersions';
import { useItemDetailsFormSections } from '../../../hooks/useItemDetailsFormSections';
import { ItemDetailsFormState, ListingFlowFormField } from '../ListingFlowTypes';

const ListingFlowProgressBar: FC = () => {
  const formState = useFormState() as ItemDetailsFormState;
  const getItemDetailsFormSectionsToShow = useItemDetailsFormSections();

  const form = useForm();

  const { currentListing } = useOwnListingVersions();
  const { publicData } = currentListing.attributes;

  const { listingFlowId, listingStartTime } = publicData || {};

  const {
    values: {
      itemType,
      originalPrice,
      numRequiredImages,
      [ListingFlowFormField.ListingFlowSectionToTimestamp]: listingFlowSectionToTimestamp = {},
      [ListingFlowFormField.DidUseQRCode]: didUseQRCode,
      ...allOtherFormValues
    },
  } = formState;

  // create a ref to only trigger a useEffect if the ref needs to be updated
  const formValuesRef = useRef(allOtherFormValues);
  if (!isEqual(formValuesRef.current, allOtherFormValues)) {
    formValuesRef.current = allOtherFormValues;
  }

  // create a ref of listingFlowSectionToTimestamp to track between renders
  const listingFlowSectionToTimestampRef = useRef(listingFlowSectionToTimestamp);
  if (!isEqual(listingFlowSectionToTimestampRef.current, listingFlowSectionToTimestamp)) {
    listingFlowSectionToTimestampRef.current = {
      ...listingFlowSectionToTimestampRef.current,
      ...listingFlowSectionToTimestamp,
    };
  }

  const itemDetailsFormSectionsToShow = getItemDetailsFormSectionsToShow(itemType);

  const relevantSections = getRelevantSections(itemDetailsFormSectionsToShow);

  const sectionsToFormValuesAndErrors = getSectionsToFormValuesAndErrors(formState);
  const completedSections = getCompletedSections(relevantSections, sectionsToFormValuesAndErrors);
  const progressBarSections = getProgressBarCompletionStates(relevantSections, completedSections);

  // tracking-related logic. Could be moved to a separate tracking component at some point
  useEffect(() => {
    const newlyCompletedSectionTimes = getNewlyCompletedSectionNameToCompletionTime(
      completedSections,
      listingFlowSectionToTimestampRef.current
    );

    if (formState.dirty && !formState.submitting && !isEmpty(newlyCompletedSectionTimes)) {
      const updatedListingFlowSectionToTimestamp = {
        ...newlyCompletedSectionTimes,
        ...listingFlowSectionToTimestampRef.current,
      };

      trackCompletedListingSections(
        listingStartTime,
        listingFlowSectionToTimestampRef.current,
        newlyCompletedSectionTimes,
        {
          itemType,
          listingFlowId,
          didUseQRCode,
        }
      );

      form.change(
        ListingFlowFormField.ListingFlowSectionToTimestamp,
        updatedListingFlowSectionToTimestamp
      );

      listingFlowSectionToTimestampRef.current = updatedListingFlowSectionToTimestamp;
    }
  }, [formValuesRef.current]);

  if (!progressBarSections.length) {
    return null;
  }

  return <ProgressBar displayedSections={progressBarSections} />;
};

export default ListingFlowProgressBar;
